import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import type { LevelGameSnapshot } from "@/core/LevelGame";
import type { BiteFeedbackEvent, Direction, Point } from "@/core/types";
import type { SkillMoment, BiteBonusResult } from "@/core/scoring/types";
import { computeSnakeVisualRuns } from "@/rendering/snakeRenderInterpolation";
import { hexToCss } from "@/ui/colors";
import { FeedbackQueue, FeedbackPriority } from "./priority";
import {
  mapBiteEventToHeadlineChain,
  mapBiteEventToNearSnake,
  mapSkillMomentToSkillFeedback,
  mapBiteBonusToSkillFeedback,
  gateOpenedHeadline,
  gateEntryHeadline,
  levelCompleteHeadline,
  starfruitGateExpiredHeadline,
  eventIntensityTier,
  rushIntensity,
  shrinkGhostSegments,
  clampToBounds,
  biteScoreDelta,
  LEVEL_COMPLETE_INTENSITY_TIER,
  type HeadlineFeedback,
  type SkillFeedback,
  type ImpactLabelPayload,
} from "./mapEvents";
import { deriveStatusBadges, type StatusBadgeId } from "./statusBadges";
import { GateGlowTweenManager, type GateGlowState } from "./gateGlowTween";
import { impactColorForEvent, SHRINK_EFFECT_COLOR, SPEED_TRIAL_COLOR, scoreFeedbackAccent, SCORE_FEEDBACK_ACCENT_COLOR } from "./palette";
import { headlineSemanticColor } from "./semanticColors";
import { HEADLINE_Y_OFFSET, SKILL_Y_OFFSET, STATUS_WORMHOLE_Y_OFFSET, STATUS_SPEED_TRIAL_Y_OFFSET, NEAR_SNAKE_MIN_Y_OFFSET } from "./laneLayout";

export interface BoardLayoutLike {
  cellSize: number;
  originX: number;
  originY: number;
  boardWidth: number;
  boardHeight: number;
}

/** This game's one shared "default/neutral" text color - the headline lane's own pre-existing color, and the HUD score text's own resting color between bumps (`pulseScoreHud()`). */
const DEFAULT_TEXT_COLOR = "#f5f7fa";

/**
 * "Tactile Impact Polish": the spatial context a bite/real-time event
 * carries alongside its typed `BiteFeedbackEvent` payload - every field
 * here is authoritative engine state read straight off the same
 * before/after snapshots `GameScene` already has at the call site, never
 * inferred or reconstructed. `atPoint`/`snakeBefore`/`snakeAfter` are
 * `null`/empty only for a purely real-time event (a completed SPEED
 * TRIAL) with no single cell to anchor a spatial effect to.
 */
export interface BiteImpactContext {
  /** The eaten fruit's cell, captured before it was removed from the board - also the snake's new head position for a normal bite. */
  atPoint: Point | null;
  wormholeAlreadyActiveBefore: boolean;
  /** Full current snake body (head first), after this move - drives the head/body pulse and RUSH's directional afterimage trail. */
  snakeAfter: readonly Point[];
  /** Full snake body before this move - only actually used for SHRINK's ghost-segment positions (`shrinkGhostSegments()`). */
  snakeBefore: readonly Point[];
  /** The current movement direction - drives RUSH's afterimage trail direction. */
  direction: Direction;
}

/**
 * PR13-C (Visual Feedback V2): the single, typed, `GameScene`-owned
 * controller for every gameplay visual-feedback element in the spec -
 * major-event headlines, near-snake bite feedback, active-effect status
 * badges, particles/graphics/tweens, message priority/queueing,
 * pause/resume, object reuse, and cleanup on death/Restart/scene
 * shutdown/scene transition. `GameScene` still owns the board/snake/HUD
 * score rendering itself (unchanged) and simply calls into this for
 * everything this PR adds or restyles - no other file creates a one-off
 * feedback text/tween of its own.
 *
 * Every transient object this controller ever creates is tracked and
 * killed by `destroy()`/`resetForNewRun()` - nothing it creates can ever
 * outlive a run or a scene. It never mutates `LevelGame`/engine state,
 * persistence, or analytics - purely observational rendering, same as
 * the code it replaces.
 */
export class VisualFeedbackController {
  private readonly scene: Phaser.Scene;
  private paused = false;

  private readonly headlineQueue = new FeedbackQueue<HeadlineFeedback>();
  private readonly skillQueue = new FeedbackQueue<SkillFeedback>();
  private readonly nearSnakeQueue = new FeedbackQueue<ImpactLabelPayload>();

  private readonly headlineText: Phaser.GameObjects.Text;
  /** New player goal clarity: the one-time "GROW AS LONG AS YOU CAN" Endless goal hint - deliberately its OWN Text object, never routed through `headlineQueue`, so it can never be pre-empted mid-fade by an unrelated gameplay headline (or vice versa) and its fade-in/hold/fade-out timing is never subject to the queue's priority/advance rules. See `showEndlessGoalHint()`. */
  private readonly goalHintText: Phaser.GameObjects.Text;
  private readonly skillText: Phaser.GameObjects.Text;
  private readonly nearSnakeText: Phaser.GameObjects.Text;
  private readonly statusTexts: Record<Exclude<StatusBadgeId, "starfruit-gate">, Phaser.GameObjects.Text>;
  private readonly wormholeSnakeOverlay: Phaser.GameObjects.Graphics;
  private readonly speedTrialEdgeOverlay: Phaser.GameObjects.Graphics;
  /** "Tactile Impact Polish": the temporary STARFRUIT gate's own countdown - positioned near the gate cell itself (not the shared top status lane), since it's local to that one board cell. */
  private readonly gateCountdownText: Phaser.GameObjects.Text;
  /** PR31 follow-up (score feedback): the small floating "+N" shown next to the HUD score on a positive bump - one pooled/reused Text object (same "kill tween, reuse" pattern as every other lane text here), never a fresh one per bite. See `pulseScoreHud()`. */
  private readonly scoreDeltaText: Phaser.GameObjects.Text;

  private readonly gateGlow: GateGlowTweenManager<Phaser.Types.Tweens.TweenBuilderConfig>;
  private readonly gateGraphicsRef: Phaser.GameObjects.Graphics;

  /** Every transient (non-pooled) object this controller has created and not yet destroyed - eat bursts, wrap flashes, frame flashes, sparkles. Swept by `destroy()`/`resetForNewRun()` so nothing outlives its run. */
  private readonly transient = new Set<Phaser.GameObjects.GameObject>();

  /** A small fixed-size pool of afterimage/trail graphics, reused round-robin (never destroyed and recreated) for RUSH's directional trail - the only effect frequent enough to warrant pooling over the transient allocate-and-destroy pattern above. */
  private readonly rushTrailPool: Phaser.GameObjects.Graphics[] = [];
  private rushTrailCursor = 0;

  private layout: BoardLayoutLike | null = null;
  private gatePosition: Point | null = null;
  /** The `topReserved` passed to the most recent `setLayout()` - kept so `positionNearSnakeAt()` can clamp against the same header-lane offsets `setLayout()` itself uses, without duplicating them as literals at the call site. */
  private topReserved = 0;
  /** Whether the near-gate countdown's own warning pulse tween is currently running - so `renderGateCountdown()` only restarts it on an actual locked/warning transition, never every frame. */
  private gateCountdownWarning = false;

  /** `?qa=feedback`-only managed delayed calls (e.g. the temp-to-permanent gate demo) - Phaser's own scene-owned timer, never a raw `setTimeout`, and explicitly canceled on `resetForNewRun()`/`destroy()` so a stale callback can never fire into a torn-down run. */
  private readonly qaTimers: Phaser.Time.TimerEvent[] = [];

  /** `?qa=feedback` only - a synthetic status badge shown while no real engine badge exists for that id (see `renderStatusLane()`). Never read/written outside QA triggers and that render method. */
  private qaBadgeOverride: Partial<Record<StatusBadgeId, { text: string; warning: boolean; color: number } | undefined>> = {};

  constructor(scene: Phaser.Scene, gateGraphics: Phaser.GameObjects.Graphics) {
    this.scene = scene;
    this.gateGraphicsRef = gateGraphics;

    this.headlineText = scene.add
      .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "22px", fontStyle: "bold", color: "#f5f7fa", align: "center" })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(41);

    this.goalHintText = scene.add
      .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "18px", fontStyle: "bold", color: "#f5f7fa", align: "center" })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(41);

    this.skillText = scene.add
      .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "16px", fontStyle: "bold", color: hexToCss(GameConfig.colors.star) })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(41);

    this.nearSnakeText = scene.add
      .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "15px", fontStyle: "bold", color: "#f5f7fa" })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(44);

    this.scoreDeltaText = scene.add
      .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "14px", fontStyle: "bold", color: "#f5f7fa" })
      .setOrigin(0, 0.5)
      .setAlpha(0)
      .setDepth(46);

    // The temporary STARFRUIT gate's own countdown is NOT in this shared
    // top-of-board status lane - it lives at `gateCountdownText`, near the
    // actual gate cell (see `positionGateCountdown()`), since it's local
    // to that one board cell rather than a whole-board state like
    // WORMHOLE/SPEED TRIAL.
    this.statusTexts = {
      wormhole: scene.add
        .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "13px", fontStyle: "bold", color: hexToCss(GameConfig.colors.wormholeCyan) })
        .setOrigin(0.5)
        .setDepth(42)
        .setVisible(false),
      "speed-trial": scene.add
        .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "13px", fontStyle: "bold", color: hexToCss(SPEED_TRIAL_COLOR) })
        .setOrigin(0.5)
        .setDepth(42)
        .setVisible(false),
    };

    this.wormholeSnakeOverlay = scene.add.graphics().setDepth(12).setVisible(false);
    this.speedTrialEdgeOverlay = scene.add.graphics().setDepth(12).setVisible(false);

    this.gateCountdownText = scene.add
      .text(0, 0, "", { fontFamily: "Arial, sans-serif", fontSize: "12px", fontStyle: "bold", color: hexToCss(GameConfig.colors.starfruitGlow), align: "center" })
      .setOrigin(0.5)
      .setDepth(42)
      .setVisible(false);

    this.gateGlow = new GateGlowTweenManager(
      { add: (config) => scene.tweens.add(config) },
      (state) => this.buildGateGlowConfig(state),
    );
  }

  // ---------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------

  /** QA-follow-up fix (Defect B): every header lane's Y offset now lives in the pure, directly-testable `laneLayout.ts` module - see its own doc for why the SKILL->WORMHOLE-badge gap widened. */
  setLayout(width: number, topReserved: number, boardLayout: BoardLayoutLike): void {
    this.layout = boardLayout;
    this.topReserved = topReserved;
    const cx = width / 2;
    this.headlineText.setPosition(cx, topReserved + HEADLINE_Y_OFFSET);
    this.goalHintText.setPosition(cx, topReserved + HEADLINE_Y_OFFSET);
    this.skillText.setPosition(cx, topReserved + SKILL_Y_OFFSET);
    this.statusTexts.wormhole.setPosition(cx, topReserved + STATUS_WORMHOLE_Y_OFFSET);
    this.statusTexts["speed-trial"].setPosition(cx, topReserved + STATUS_SPEED_TRIAL_Y_OFFSET);
    this.positionGateCountdown();
  }

  /** The exit gate's cell - fixed for the whole level, set once from `GameScene.create()`. Drives the STARFRUIT eat-to-gate energy travel's destination and the near-gate countdown's position - never inferred from the gate's drawn pixel rect. */
  setGatePosition(point: Point): void {
    this.gatePosition = point;
    this.positionGateCountdown();
  }

  /** Places the temporary-gate countdown just above (or, if that would run off the top of the board, just below) the actual gate cell, clamped inside the board's own horizontal bounds - "near the gate itself", never covering the gate cell or a neighboring cell the player might need to see. */
  private positionGateCountdown(): void {
    if (!this.layout || !this.gatePosition) return;
    const cell = this.layout.cellSize;
    const gateCx = this.layout.originX + this.gatePosition.x * cell + cell / 2;
    const gateCy = this.layout.originY + this.gatePosition.y * cell + cell / 2;
    const above = gateCy - cell * 0.9;
    const y = above < this.layout.originY + 10 ? gateCy + cell * 0.9 : above;
    const clamped = clampToBounds(
      { x: gateCx, y },
      {
        minX: this.layout.originX + 30,
        maxX: this.layout.originX + this.layout.boardWidth - 30,
        minY: this.layout.originY + 10,
        maxY: this.layout.originY + this.layout.boardHeight - 10,
      },
    );
    this.gateCountdownText.setPosition(clamped.x, clamped.y);
  }

  // ---------------------------------------------------------------------
  // New player goal clarity
  // ---------------------------------------------------------------------

  /**
   * The one-time, non-blocking "GROW AS LONG AS YOU CAN" Endless goal
   * hint - `GameScene` calls this exactly once, from `completeReadyGate()`,
   * on a player's first-ever Endless run. A plain fade-in/hold/fade-out
   * tween on its own Text object: never pauses the scene, never touches
   * input/movement, and is never itself queued/pre-empted the way a real
   * gameplay headline is (see `goalHintText`'s own doc). ~2s total
   * (220ms in + 1600ms hold + 220ms out), within the ~1.5-2.5s target.
   *
   * `onSettled` (optional) fires once, when the tween naturally completes
   * (never on an interrupted/killed tween - a Restart mid-fade must never
   * fire it) - `GameScene` uses this to reveal the persistent objective
   * label in the emphasis's place, so the two are never visible together.
   */
  showEndlessGoalHint(onSettled?: () => void): void {
    this.scene.tweens.killTweensOf(this.goalHintText);
    this.goalHintText.setText("GROW AS LONG AS YOU CAN").setAlpha(0).setScale(0.9);
    this.scene.tweens.add({
      targets: this.goalHintText,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.9, to: 1 },
      duration: 220,
      hold: 1600,
      yoyo: true,
      ease: "Sine.easeInOut",
      onComplete: onSettled,
    });
  }

  // ---------------------------------------------------------------------
  // Pause
  // ---------------------------------------------------------------------

  /**
   * Every manually-redrawn-per-frame visual (status badges, the gate
   * countdown number, the WORMHOLE snake overlay, the SPEED TRIAL edge
   * streak) already freezes correctly just by `update()` no longer being
   * called while paused. The two *tween-driven* repeat:-1 pulses (the
   * gate glow, and the gate countdown's own warning pulse) don't get that
   * for free - a Phaser tween keeps animating on the engine's own clock
   * regardless of this flag - so they're explicitly paused/resumed here,
   * in place, never killed and recreated.
   */
  onPauseChange(paused: boolean): void {
    this.paused = paused;
    this.gateGlow.setPaused(paused);
    if (this.gateCountdownWarning) {
      if (paused) this.scene.tweens.getTweensOf(this.gateCountdownText).forEach((t) => t.pause());
      else this.scene.tweens.getTweensOf(this.gateCountdownText).forEach((t) => t.resume());
    }
  }

  // ---------------------------------------------------------------------
  // Per-frame update - only ever called while playing and unpaused
  // (`GameScene.update()` already gates every real-time call on that), so
  // every timer/warning presentation here freezes automatically the
  // instant the caller stops calling it, exactly like the engine's own
  // real-time countdowns.
  // ---------------------------------------------------------------------

  /**
   * PR32 (Snake Movement Smoothing): `renderSnake` is the SAME render-only
   * interpolated snake positions `GameScene` draws the main snake at this
   * frame (see `GameScene.renderSnakePosition()`) - never `snapshot.snake`
   * directly - so the WORMHOLE outline below never visually detaches from
   * the actual drawn snake. Same length/order as `snapshot.snake`; values
   * may be fractional grid coordinates, never gameplay state.
   */
  update(deltaMs: number, snapshot: LevelGameSnapshot, renderSnake: readonly Point[]): void {
    if (this.paused) return;

    if (this.headlineQueue.tick(deltaMs)) this.renderHeadlineLane();
    if (this.skillQueue.tick(deltaMs)) this.renderSkillLane();
    if (this.nearSnakeQueue.tick(deltaMs)) this.renderNearSnakeLane();

    this.renderStatusLane(snapshot);
    this.renderWormholeSnakeOverlay(snapshot, renderSnake);
    this.renderSpeedTrialEdgeOverlay(snapshot);
  }

  // ---------------------------------------------------------------------
  // Bite / real-time events - the shared tactile bite-impact sequence
  // ---------------------------------------------------------------------

  /**
   * The single entry point for every fruit/real-time event's spatial
   * reaction - headline/near-bite-label queueing (unchanged from before
   * this pass) plus the new tactile impact sequence: a compression pop, a
   * colored ring scaled by `eventIntensityTier()`, a head pulse, and (for
   * specific outcomes) a body pulse, RUSH afterimage trail, SHRINK ghost
   * segments, or the STARFRUIT eat-to-gate energy travel. Every spatial
   * primitive here is purely observational - it never mutates `LevelGame`
   * state, and never delays or alters the gameplay result that already
   * resolved in the engine before this is called.
   */
  onBiteEvent(event: BiteFeedbackEvent, ctx: BiteImpactContext): void {
    const chain = mapBiteEventToHeadlineChain(event, { isWormholeRefresh: ctx.wormholeAlreadyActiveBefore });
    if (chain.length > 0) {
      this.headlineQueue.enqueueChain(chain);
      this.renderHeadlineLane();
    }

    const nearSnake = mapBiteEventToNearSnake(event);
    if (nearSnake) {
      this.nearSnakeQueue.enqueue(nearSnake);
      if (ctx.atPoint) this.positionNearSnakeAt(ctx.atPoint);
      this.renderNearSnakeLane();
    }

    if (ctx.atPoint) this.playBiteImpact(event, ctx.atPoint, ctx);
  }

  /** The shared tactile impact sequence: compression pop + ring at the eaten cell (both scaled by the event's intensity tier), a brief head pulse, and any outcome-specific extra (JACKPOT's body pulse, SHRINK's ghost segments, RUSH's afterimage trail, STARFRUIT's gate energy travel). */
  private playBiteImpact(event: BiteFeedbackEvent, atPoint: Point, ctx: BiteImpactContext): void {
    const tier = eventIntensityTier(event);
    const color = impactColorForEvent(event);
    this.playPop(atPoint, color, tier);
    this.playImpactRing(atPoint, color, tier);
    const head = ctx.snakeAfter[0] ?? atPoint;
    this.playPulse(head, color, false, 0.6 + tier * 0.03);

    if (event.kind === "rush") {
      this.playRushTrail(ctx.snakeAfter, ctx.direction, rushIntensity(event.stack));
      return;
    }
    if (event.kind === "starfruit") {
      this.playStarburst(atPoint);
      if (this.gatePosition) this.playEnergyTravel(atPoint, this.gatePosition, GameConfig.colors.starfruitGlow);
      return;
    }
    if (event.kind !== "chaos") return;
    switch (event.outcome) {
      case "jackpot":
        this.playBurstRing(atPoint, GameConfig.colors.star, 4);
        this.playBodyPulse(ctx.snakeAfter, GameConfig.colors.star);
        this.playFrameFlash(GameConfig.colors.star);
        break;
      case "shrink": {
        const ghosts = shrinkGhostSegments(ctx.snakeBefore, event.segmentsRemoved);
        if (ghosts.length > 0) this.playShrinkGhosts(ghosts, head);
        else this.playPulse(atPoint, SHRINK_EFFECT_COLOR, true, 0.8);
        break;
      }
      case "wormhole":
        this.playWormholeActivationPulse();
        // PR31 (visual polish - CHAOS feedback): a brief board-frame
        // flash in WORMHOLE's own cyan, same treatment JACKPOT already
        // gets in its own color - reuses `playFrameFlash()` as-is, no new
        // effect. WORMHOLE was previously the one big CHAOS outcome with
        // no board-level flash at all, despite being just as significant
        // a moment as JACKPOT.
        this.playFrameFlash(GameConfig.colors.wormholeCyan);
        break;
      case "speed-trial":
        // PR31 (visual polish - CHAOS feedback): a small burst ring in
        // SPEED TRIAL's own blue - reuses the exact primitive JACKPOT's
        // burst ring already uses, just fewer rays (3 vs 4) so it reads
        // as a lesser moment than JACKPOT's, not an equally big one.
        // Previously SPEED TRIAL got nothing beyond the shared base pop/
        // ring/pulse every bite already gets.
        this.playBurstRing(atPoint, GameConfig.colors.speedTrialBlue, 3);
        break;
      case "score-hit":
        break;
    }
  }

  onSkillMoment(moment: SkillMoment): void {
    this.skillQueue.enqueueChain(mapSkillMomentToSkillFeedback(moment));
    this.renderSkillLane();
  }

  onBiteBonus(bonus: BiteBonusResult): void {
    this.skillQueue.enqueue(mapBiteBonusToSkillFeedback(bonus));
    this.renderSkillLane();
  }

  /** A wrap's own exit ripple (`fromCell`), entry ripple (`toCell`), and a short head afterimage connecting the two - all from the authoritative successful-wrap signal already gated at the call site (`LevelGame.getLastWormholeWrapSuccess()`), never a large line drawn straight across the board. */
  onWormholeWrap(fromCell: Point, toCell: Point): void {
    this.playPulse(fromCell, GameConfig.colors.wormholeViolet, false);
    this.playPulse(toCell, GameConfig.colors.wormholeCyan, false);
    this.playEnergyTravel(fromCell, toCell, GameConfig.colors.wormholeCyan, () => {});
    this.playFrameFlash(GameConfig.colors.wormholeCyan);
  }

  /**
   * Portals V1 (Saturn World): a clear entry pulse and a clear exit pulse,
   * same color (reinforcing "one pair"), plus a brief frame flash -
   * deliberately NOT an energy-travel beam between the two cells (unlike
   * `onWormholeWrap()`'s short local spark): a portal's two endpoints can
   * be the full width of the board apart, and a beam spanning that
   * distance would read as exactly the kind of ghost-trail/guide-line
   * visualization held in reserve for later gameplay QA, not shipped in
   * V1. The persistent pair markers (`GameScene.renderPortals()`) are
   * what carries paired-color identity; this is only the momentary
   * traversal cue.
   */
  onPortalTraversal(entryCell: Point, exitCell: Point): void {
    const color = GameConfig.colors.portalPairs[0];
    this.playPulse(entryCell, color, false);
    this.playPulse(exitCell, color, false);
    this.playFrameFlash(color);
  }

  /**
   * BLACK HOLE V1 (Saturn World): a single converging (not expanding)
   * pulse at the black-hole cell the instant the head enters it, plus a
   * dark frame flash - visually distinct from every expanding pulse used
   * elsewhere (WORMHOLE wrap, portal traversal, bite impacts), matching
   * "being pulled in" rather than "bursting out". The actual segment-by-
   * segment consumption is driven by `GameScene.startBlackHoleConsumption()`
   * redrawing the snake directly; this is only the one-shot entry cue.
   */
  onBlackHoleConsume(cell: Point): void {
    this.playPulse(cell, GameConfig.colors.blackHoleRing, true, 1.6);
    this.playFrameFlash(GameConfig.colors.blackHoleRing);
  }

  onStarfruitGateExpired(): void {
    this.headlineQueue.enqueue(starfruitGateExpiredHeadline());
    this.renderHeadlineLane();
    this.playFrameFlash(GameConfig.colors.starfruitGlow);
  }

  /** The gate just became enterable (real target length reached, or a temporary STARFRUIT gate opened) - a majorEvent-tier headline, distinct from actually walking into it. */
  onGateOpened(): void {
    this.headlineQueue.enqueue(gateOpenedHeadline());
    this.renderHeadlineLane();
  }

  /** Gate entry (the head just walked into the already-open gate) - clears every ambient/skill/near-snake effect and status badge (they're meaningless during the automatic exit walk), shows its own headline, and gives one short tunnel flash. Called once, on the first exiting step only - `playGateEntryFlash()` is the separate, repeated per-step pulse for the rest of the walk. Deliberately never touches the gate-glow tween itself - it keeps gently pulsing through the exit walk, exactly like the code this replaces. */
  onGateEntry(): void {
    this.clearAmbientOnly();
    this.headlineQueue.enqueue(gateEntryHeadline());
    this.renderHeadlineLane();
    this.playFrameFlash(GameConfig.colors.gateOpenGlow);
  }

  /** Death - the existing Game Over overlay (unchanged by this PR) carries the actual message, so this only needs to clear every active/queued feedback effect and give one restrained red impulse. */
  onDeath(): void {
    this.clearAmbientOnly();
    this.headlineQueue.clear();
    this.renderHeadlineLane();
    this.playFrameFlash(GameConfig.colors.danger);
  }

  /** Level complete - a short, non-blocking "LEVEL COMPLETE!" headline moment overlapping the existing overlay's own fade-in, plus the hierarchy's single strongest board/gate pulse (tier `LEVEL_COMPLETE_INTENSITY_TIER`, above JACKPOT/STARFRUIT); the overlay itself (unchanged by this PR) still carries the Bite/Skill/Pace breakdown and stars. */
  onLevelComplete(): void {
    this.clearAmbientOnly();
    this.headlineQueue.enqueue(levelCompleteHeadline());
    this.renderHeadlineLane();
    this.playFrameFlash(GameConfig.colors.star);
    if (this.gatePosition) {
      this.playGateRipple(this.gatePosition, GameConfig.colors.gateOpenGlow);
      this.playImpactRing(this.gatePosition, GameConfig.colors.star, LEVEL_COMPLETE_INTENSITY_TIER);
    }
  }

  /** A short scale-pop emphasis on an already-rendered overlay line (e.g. "NEW STAR RECORD!"/"NEW HIGH SCORE!") - never changes its text/value, purely a motion accent so either (or both, independently) reads as a real achievement. Kills any tween already running on `target` first, the same "kill before replace" rule as everything else here. */
  emphasize(target: Phaser.GameObjects.Text): void {
    this.scene.tweens.killTweensOf(target);
    target.setScale(0.85);
    this.scene.tweens.add({ targets: target, scale: { from: 0.85, to: 1.08 }, duration: 220, ease: "Back.easeOut" });
    this.scene.tweens.add({ targets: target, scale: 1, duration: 120, delay: 220, ease: "Sine.easeOut" });
  }

  // ---------------------------------------------------------------------
  // Gate glow lifecycle (PR13-C fix: exactly one tween per gate state)
  // ---------------------------------------------------------------------

  syncGateGlow(snapshot: LevelGameSnapshot): GateGlowState {
    const isStarfruitGate = snapshot.starfruitGateRemainingMs !== null;
    const warning = isStarfruitGate && snapshot.starfruitGateRemainingMs !== null && snapshot.starfruitGateRemainingMs <= 3000;
    const state: GateGlowState = !snapshot.isGateOpen ? "locked" : warning ? "warning" : isStarfruitGate ? "open-temporary" : "open-permanent";
    this.gateGlow.setState(state);
    return state;
  }

  private buildGateGlowConfig(state: Exclude<GateGlowState, "locked">): Phaser.Types.Tweens.TweenBuilderConfig {
    this.gateGraphicsRef.setAlpha(1).setScale(1);
    const fast = state === "warning";
    return {
      targets: this.gateGraphicsRef,
      alpha: { from: 1, to: fast ? 0.5 : 0.7 },
      duration: fast ? 220 : 420,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    };
  }

  /** A small scale pulse on the gate itself - called on every step of the automatic exit walk (mirrors the per-step "swallow pulse" this replaces), separate from `onGateEntry()`'s own one-time flash on the first such step. */
  playGateEntryFlash(): void {
    this.gateGraphicsRef.setScale(1);
    this.scene.tweens.add({ targets: this.gateGraphicsRef, scale: { from: 1, to: 1.22 }, duration: 140, yoyo: true, ease: "Sine.easeOut" });
  }

  // ---------------------------------------------------------------------
  // Lane rendering
  // ---------------------------------------------------------------------

  private renderHeadlineLane(): void {
    this.scene.tweens.killTweensOf(this.headlineText);
    const item = this.headlineQueue.current;
    if (!item || !item.payload.text) {
      this.headlineText.setAlpha(0);
      return;
    }
    // PR31 follow-up (semantic CHAOS result colors): color communicates
    // WHAT KIND of result this is, on top of the text itself - never the
    // only distinction (the text is always the explicit, accessible
    // source of truth). See `semanticColors.ts`'s own doc for the table.
    const semanticColor = headlineSemanticColor(item.payload.kind);
    this.headlineText.setColor(semanticColor !== null ? hexToCss(semanticColor) : DEFAULT_TEXT_COLOR);
    this.headlineText.setText(item.payload.text).setAlpha(0).setScale(0.85);
    this.scene.tweens.add({
      targets: this.headlineText,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.85, to: 1 },
      duration: 160,
      ease: "Back.easeOut",
    });
  }

  private renderSkillLane(): void {
    this.scene.tweens.killTweensOf(this.skillText);
    const item = this.skillQueue.current;
    if (!item) {
      this.skillText.setAlpha(0);
      return;
    }
    this.skillText.setText(item.payload.text).setAlpha(0).setScale(0.85);
    this.scene.tweens.add({ targets: this.skillText, alpha: { from: 0, to: 1 }, scale: { from: 0.85, to: 1 }, duration: 140, ease: "Back.easeOut" });
  }

  /** QA follow-up (visual correctness): SHRINK's own negative delta label uses `shrink` (CHAOS purple), never the generic `loss` (red) legacy SCORE HIT still uses - SHRINK must render with no red anywhere. */
  private static readonly IMPACT_LABEL_COLOR: Record<ImpactLabelPayload["kind"], number> = {
    safe: GameConfig.colors.biteSafe,
    rush: GameConfig.colors.biteRush,
    gain: GameConfig.colors.star,
    loss: GameConfig.colors.danger,
    shrink: SHRINK_EFFECT_COLOR,
  };

  private renderNearSnakeLane(): void {
    this.scene.tweens.killTweensOf(this.nearSnakeText);
    const item = this.nearSnakeQueue.current;
    if (!item) {
      this.nearSnakeText.setAlpha(0);
      return;
    }
    this.nearSnakeText.setText(item.payload.text).setColor(hexToCss(VisualFeedbackController.IMPACT_LABEL_COLOR[item.payload.kind])).setAlpha(0).setScale(0.9);
    const baseY = this.nearSnakeText.y;
    this.scene.tweens.add({ targets: this.nearSnakeText, alpha: { from: 0, to: 1 }, y: { from: baseY, to: baseY - 6 }, duration: 120, ease: "Sine.easeOut" });
  }

  /**
   * Positions and shows the near-snake text right at the eaten fruit's
   * cell for the duration of this queue item - called from `onBiteEvent`
   * when the caller has a real cell to anchor to. Clamped to never render
   * above the persistent header lane stack (see `NEAR_SNAKE_MIN_Y_OFFSET`)
   * - a bite near the board's own top rows would otherwise place this
   * dynamic label directly on top of the fixed WORMHOLE/SPEED TRIAL status
   * badges, the real WORMHOLE text collision manual QA found.
   */
  private positionNearSnakeAt(point: Point): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const pixel = { x: this.layout.originX + point.x * cell, y: this.layout.originY + point.y * cell };
    const minY = this.topReserved + NEAR_SNAKE_MIN_Y_OFFSET;
    this.nearSnakeText.setPosition(pixel.x + cell / 2, Math.max(pixel.y - cell * 0.5, minY));
  }

  /**
   * Real authoritative engine data always wins. Every frame recomputes
   * badges straight from `snapshot`, which would otherwise immediately
   * overwrite a `?qa=feedback`-only synthetic badge (`qaBadgeOverride` -
   * see `triggerQa()`) back to hidden the very next frame, since the real
   * engine has no actual WORMHOLE/SPEED TRIAL/STARFRUIT gate running in
   * that demo. `qaBadgeOverride` only ever fills in for an id with no
   * real badge this frame, and is cleared the instant a real one exists.
   */
  private renderStatusLane(snapshot: LevelGameSnapshot): void {
    const badges = deriveStatusBadges(snapshot);
    const ids: Exclude<StatusBadgeId, "starfruit-gate">[] = ["wormhole", "speed-trial"];
    for (const id of ids) {
      const real = badges.find((b) => b.id === id);
      const text = this.statusTexts[id];
      if (real) {
        this.qaBadgeOverride[id] = undefined;
        text.setVisible(true).setText(real.text);
        const baseColor = id === "wormhole" ? GameConfig.colors.wormholeCyan : SPEED_TRIAL_COLOR;
        text.setColor(hexToCss(real.warning ? GameConfig.colors.danger : baseColor));
        continue;
      }
      const override = this.qaBadgeOverride[id];
      if (override) {
        text.setVisible(true).setText(override.text).setColor(hexToCss(override.warning ? GameConfig.colors.danger : override.color));
      } else {
        text.setVisible(false);
      }
    }
    this.renderGateCountdown(badges.find((b) => b.id === "starfruit-gate"));
  }

  /** The temporary STARFRUIT gate's own near-gate countdown - authoritative remaining time only, same "real data always wins over a QA override" rule as `renderStatusLane()`. Pulses increasingly (a faster scale pulse, restarted only on the locked/warning transition, never every frame) during the final three seconds, in sync with the gate glow's own faster warning pulse. */
  private renderGateCountdown(real: ReturnType<typeof deriveStatusBadges>[number] | undefined): void {
    const override = this.qaBadgeOverride["starfruit-gate"];
    const badge = real ?? (override ? { text: override.text, warning: override.warning } : null);
    if (real) this.qaBadgeOverride["starfruit-gate"] = undefined;

    if (!badge) {
      if (this.gateCountdownWarning) {
        this.scene.tweens.killTweensOf(this.gateCountdownText);
        this.gateCountdownText.setScale(1);
        this.gateCountdownWarning = false;
      }
      this.gateCountdownText.setVisible(false);
      return;
    }
    this.gateCountdownText.setVisible(true).setText(badge.text).setColor(hexToCss(badge.warning ? GameConfig.colors.danger : GameConfig.colors.starfruitGlow));
    if (badge.warning !== this.gateCountdownWarning) {
      this.gateCountdownWarning = badge.warning;
      this.scene.tweens.killTweensOf(this.gateCountdownText);
      this.gateCountdownText.setScale(1);
      if (badge.warning) {
        this.scene.tweens.add({ targets: this.gateCountdownText, scale: { from: 1, to: 1.18 }, duration: 260, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });
      }
    }
  }

  private hideStatusLane(): void {
    this.qaBadgeOverride = {};
    for (const id of ["wormhole", "speed-trial"] as const) this.statusTexts[id].setVisible(false);
    this.scene.tweens.killTweensOf(this.gateCountdownText);
    this.gateCountdownText.setVisible(false).setScale(1);
    this.gateCountdownWarning = false;
  }

  /**
   * Restrained cyan outline along the snake's own cells while WORMHOLE is
   * active - additive-only (drawn on top of, never replacing, the
   * existing snake graphics), so the snake's own shape/fruit visibility is
   * always preserved.
   *
   * PR31 follow-up (visual-language pass, WORMHOLE board-frame feedback):
   * the SAME per-frame pulse also strokes the board's own outer frame in
   * WORMHOLE's cyan/turquoise, communicating "the walls are traversable"
   * - it only ever supplements, never replaces, the existing WORMHOLE
   * status badge/timer (`renderStatusLane()`), and both readings share the
   * one existing `warning` flag, so the frame reinforces the same final
   * three-second warning the status badge/snake outline already show
   * (switching to danger red, pulsing faster) rather than inventing a
   * second countdown presentation. No tween anywhere in this method - the
   * pulse is pure `Math.sin(scene.time.now)` math recomputed every call,
   * so a WORMHOLE refresh (this just keeps reading a non-null
   * `wormholeRemainingMs`) can never stack a duplicate animation, and
   * expiry (`wormholeRemainingMs` goes `null`) always leaves the frame
   * fully cleared - never a stale glow.
   */
  private renderWormholeSnakeOverlay(snapshot: LevelGameSnapshot, renderSnake: readonly Point[]): void {
    const g = this.wormholeSnakeOverlay;
    if (snapshot.wormholeRemainingMs === null || !this.layout) {
      g.setVisible(false).clear();
      return;
    }
    const warning = snapshot.wormholeRemainingMs <= 3000;
    const pulse = 0.35 + 0.25 * Math.sin(this.scene.time.now / (warning ? 130 : 260));
    g.setVisible(true).clear();
    const cell = this.layout.cellSize;
    // PR32 follow-up (continuous-body art pass): the main snake is now
    // one continuous body, not a row of separately-outlined cells - this
    // outline follows the SAME geometry (computeSnakeVisualRuns(), the
    // exact same pure function GameScene's own body renderer uses),
    // stroking a single path per run instead of a rounded-rect per cell.
    // A run boundary (a genuine portal/WORMHOLE discontinuity) still
    // never gets a stroke connecting it to another run - the snake may
    // correctly appear as two separate outlined pieces on opposite board
    // edges mid-wrap, exactly like the main body itself.
    g.lineStyle(3, warning ? GameConfig.colors.danger : GameConfig.colors.wormholeCyan, Math.max(0.2, pulse));
    // Captured into a local (not read via `this.layout` inside the
    // forEach closure below) - TS's null-narrowing from the guard above
    // doesn't persist into a nested function body.
    const originX = this.layout.originX;
    const originY = this.layout.originY;
    for (const run of computeSnakeVisualRuns(renderSnake)) {
      if (run.length === 0) continue;
      if (run.length === 1) {
        const p = run[0];
        g.strokeCircle(originX + p.x * cell + cell / 2, originY + p.y * cell + cell / 2, cell * 0.32);
        continue;
      }
      g.beginPath();
      run.forEach((p, i) => {
        const px = originX + p.x * cell + cell / 2;
        const py = originY + p.y * cell + cell / 2;
        if (i === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      });
      g.strokePath();
    }
    g.lineStyle(2.5, warning ? GameConfig.colors.danger : GameConfig.colors.wormholeCyan, Math.max(0.25, pulse) * 0.6);
    g.strokeRoundedRect(this.layout.originX - 3, this.layout.originY - 3, this.layout.boardWidth + 6, this.layout.boardHeight + 6, 14);
  }

  // ---------------------------------------------------------------------
  // Transient graphics primitives (particles/graphics) - ported unchanged
  // in look-and-feel from the code this controller replaces, now tracked
  // for guaranteed cleanup.
  // ---------------------------------------------------------------------

  private cellCenter(atCell: Point): { x: number; y: number } {
    const cell = this.layout!.cellSize;
    return { x: this.layout!.originX + atCell.x * cell + cell / 2, y: this.layout!.originY + atCell.y * cell + cell / 2 };
  }

  private playPulse(atCell: Point, color: number, contract: boolean, sizeMultiplier = 1): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const { x: cx, y: cy } = this.cellCenter(atCell);
    const burst = this.scene.add.graphics({ x: cx, y: cy });
    burst.fillStyle(color, 0.8);
    burst.fillCircle(0, 0, cell * 0.36 * sizeMultiplier);
    this.track(burst);
    this.scene.tweens.add({
      targets: burst,
      scale: contract ? { from: 1.3, to: 0.2 } : { from: 1, to: 2.2 },
      alpha: { from: 0.8, to: 0 },
      duration: 260,
      ease: "Cubic.easeOut",
      onComplete: () => this.destroyTracked(burst),
    });
  }

  /** A very short compression "pop" (scale-down-then-up) at the eaten cell - the first beat of the shared bite-impact sequence, before the expanding ring. Scaled by `tier` so SAFE barely registers while JACKPOT/STARFRUIT clearly do. */
  private playPop(atCell: Point, color: number, tier: number): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const { x: cx, y: cy } = this.cellCenter(atCell);
    const size = cell * (0.3 + Math.min(tier, 8) * 0.02);
    const g = this.scene.add.graphics({ x: cx, y: cy });
    g.fillStyle(color, 0.9);
    g.fillCircle(0, 0, size);
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      scale: { from: 0.6, to: 1 },
      duration: 90,
      yoyo: true,
      ease: "Sine.easeOut",
      onComplete: () => this.destroyTracked(g),
    });
  }

  /** The expanding colored ring at the eaten cell - the shared sequence's second beat. Radius/brightness/duration all scale modestly with `tier` (1..9), never dramatically enough to obscure the route at any tier. */
  private playImpactRing(atCell: Point, color: number, tier: number): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const { x: cx, y: cy } = this.cellCenter(atCell);
    const t = Math.min(Math.max(tier, 1), 9);
    const g = this.scene.add.graphics({ x: cx, y: cy });
    g.lineStyle(2 + t * 0.2, color, 0.5 + t * 0.04);
    g.strokeCircle(0, 0, cell * 0.3);
    g.setAlpha(0);
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      alpha: { from: 0.9, to: 0 },
      scale: { from: 0.5, to: 1 + t * 0.12 },
      duration: 180 + t * 15,
      ease: "Cubic.easeOut",
      onComplete: () => this.destroyTracked(g),
    });
  }

  private playBurstRing(atCell: Point, color: number, rays: number): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const cx = this.layout.originX + atCell.x * cell + cell / 2;
    const cy = this.layout.originY + atCell.y * cell + cell / 2;
    const g = this.scene.add.graphics({ x: cx, y: cy });
    g.lineStyle(3, color, 0.9);
    for (let i = 0; i < rays * 3; i++) {
      const angle = (Math.PI * 2 * i) / (rays * 3);
      g.lineBetween(0, 0, Math.cos(angle) * cell * 0.2, Math.sin(angle) * cell * 0.2);
    }
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      scale: { from: 0.6, to: 2 },
      alpha: { from: 0.9, to: 0 },
      duration: 320,
      ease: "Cubic.easeOut",
      onComplete: () => this.destroyTracked(g),
    });
  }

  private playStarburst(atCell: Point): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const cx = this.layout.originX + atCell.x * cell + cell / 2;
    const cy = this.layout.originY + atCell.y * cell + cell / 2;
    const g = this.scene.add.graphics({ x: cx, y: cy });
    g.fillStyle(GameConfig.colors.starfruitGlow, 0.9);
    g.fillCircle(0, 0, cell * 0.4);
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      alpha: { from: 0.9, to: 0 },
      scale: { from: 0.6, to: 2.4 },
      angle: { from: 0, to: 45 },
      duration: 420,
      ease: "Cubic.easeOut",
      onComplete: () => this.destroyTracked(g),
    });
  }

  private playFrameFlash(color: number): void {
    if (!this.layout) return;
    const g = this.scene.add.graphics();
    g.lineStyle(4, color, 1);
    g.strokeRoundedRect(this.layout.originX - 4, this.layout.originY - 4, this.layout.boardWidth + 8, this.layout.boardHeight + 8, 14);
    g.setAlpha(0);
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      alpha: { from: 0, to: 0.9 },
      duration: 130,
      yoyo: true,
      onComplete: () => this.destroyTracked(g),
    });
  }

  /** RUSH's directional afterimage trail - a small pooled set of fading squares laid behind the head along the current movement direction, count/lifetime/brightness scaled by `intensity` (1..5). Pooled (never destroyed/recreated) since RUSH bites can fire in quick succession; each slot's own tween is killed before it's reused. */
  private playRushTrail(snakeAfter: readonly Point[], direction: Direction, intensity: number): void {
    if (!this.layout) return;
    const head = snakeAfter[0];
    if (!head) return;
    const cell = this.layout.cellSize;
    // Trails BEHIND the head along the current movement direction (the
    // opposite of the direction vector), never ahead of it - so it never
    // obscures the route the snake is actually about to take.
    const behind = direction === "left" ? { x: 1, y: 0 } : direction === "right" ? { x: -1, y: 0 } : direction === "up" ? { x: 0, y: 1 } : { x: 0, y: -1 };
    const count = intensity; // 1..5 - directly the escalation the spec asks for.
    for (let i = 1; i <= count; i++) {
      const trailCell = { x: head.x + behind.x * i, y: head.y + behind.y * i };
      const { x: cx, y: cy } = this.cellCenter(trailCell);
      const g = this.nextRushTrailSlot();
      this.scene.tweens.killTweensOf(g);
      g.setPosition(cx, cy).clear();
      g.fillStyle(GameConfig.colors.biteRush, 0.5);
      g.fillRoundedRect(-cell * 0.28, -cell * 0.28, cell * 0.56, cell * 0.56, cell * 0.15);
      g.setVisible(true).setAlpha(0.55 - i * 0.06).setScale(1);
      this.scene.tweens.add({
        targets: g,
        alpha: 0,
        scale: 0.6,
        duration: 160 + intensity * 20,
        delay: i * 25,
        ease: "Cubic.easeOut",
        onComplete: () => g.setVisible(false),
      });
    }
  }

  private nextRushTrailSlot(): Phaser.GameObjects.Graphics {
    const poolSize = 5;
    if (this.rushTrailPool.length < poolSize) {
      const g = this.scene.add.graphics().setDepth(13).setVisible(false);
      this.rushTrailPool.push(g);
      return g;
    }
    const g = this.rushTrailPool[this.rushTrailCursor % this.rushTrailPool.length];
    this.rushTrailCursor++;
    return g;
  }

  /** A brief colored pulse travelling from head to tail along the current snake body - JACKPOT's own "the reward runs through the whole snake" beat. A single transient overlay graphic, redrawn per-segment-offset via a proxy tween rather than per-segment objects, so it can never scale badly with a long snake. */
  private playBodyPulse(snakeAfter: readonly Point[], color: number): void {
    if (!this.layout || snakeAfter.length === 0) return;
    const cell = this.layout.cellSize;
    const g = this.scene.add.graphics().setDepth(13);
    this.track(g);
    const proxy = { t: 0 };
    this.scene.tweens.add({
      targets: proxy,
      t: 1,
      duration: 260,
      ease: "Cubic.easeOut",
      onUpdate: () => {
        g.clear();
        const litCount = Math.max(1, Math.round(snakeAfter.length * proxy.t));
        const alpha = 0.55 * (1 - proxy.t);
        g.fillStyle(color, Math.max(0, alpha));
        for (let i = 0; i < litCount && i < snakeAfter.length; i++) {
          const { x, y } = this.cellCenter(snakeAfter[i]);
          g.fillRoundedRect(x - cell * 0.3, y - cell * 0.3, cell * 0.6, cell * 0.6, cell * 0.2);
        }
      },
      onComplete: () => this.destroyTracked(g),
    });
  }

  /** SHRINK's ghost segments - one fading marker per actually-removed tail cell (never more than `points.length`, which is already clamped to what the engine really removed), each contracting toward the current head so the loss visibly "returns" to the snake rather than just vanishing in place. */
  private playShrinkGhosts(points: readonly Point[], head: Point): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const headPixel = this.cellCenter(head);
    points.forEach((point, i) => {
      const { x, y } = this.cellCenter(point);
      const g = this.scene.add.graphics({ x, y }).setDepth(13);
      g.fillStyle(SHRINK_EFFECT_COLOR, 0.6);
      g.fillRoundedRect(-cell * 0.32, -cell * 0.32, cell * 0.64, cell * 0.64, cell * 0.18);
      this.track(g);
      this.scene.tweens.add({
        targets: g,
        x: headPixel.x,
        y: headPixel.y,
        scale: { from: 1, to: 0.15 },
        alpha: { from: 0.6, to: 0 },
        duration: 260,
        delay: i * 40,
        ease: "Cubic.easeIn",
        onComplete: () => this.destroyTracked(g),
      });
    });
  }

  /** A fast energy packet travelling from `fromCell` to `toCell` (STARFRUIT's own eaten-fruit-to-gate connector, and WORMHOLE wrap's disappearance-to-re-entry afterimage) - purely cosmetic and never blocking: the real state (gate open, wrap committed) already happened in the engine before this plays. Calls `onArrive` (a gate ripple, for STARFRUIT) when the tween completes. */
  private playEnergyTravel(fromCell: Point, toCell: Point, color: number, onArrive?: () => void): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const from = this.cellCenter(fromCell);
    const to = this.cellCenter(toCell);
    const g = this.scene.add.graphics({ x: from.x, y: from.y }).setDepth(14);
    g.fillStyle(color, 0.95);
    g.fillCircle(0, 0, cell * 0.18);
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      x: to.x,
      y: to.y,
      duration: 320,
      ease: "Cubic.easeIn",
      onComplete: () => {
        this.destroyTracked(g);
        if (onArrive) onArrive();
        else this.playGateRipple(toCell, color);
      },
    });
  }

  /** An expanding ring centered exactly on the gate cell - "the gate emits an opening ripple/glow" the instant STARFRUIT's energy packet arrives. */
  private playGateRipple(atCell: Point, color: number): void {
    if (!this.layout) return;
    const cell = this.layout.cellSize;
    const { x, y } = this.cellCenter(atCell);
    const g = this.scene.add.graphics({ x, y }).setDepth(14);
    g.lineStyle(3, color, 0.9);
    g.strokeCircle(0, 0, cell * 0.35);
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      scale: { from: 0.6, to: 2.6 },
      alpha: { from: 0.9, to: 0 },
      duration: 360,
      ease: "Cubic.easeOut",
      onComplete: () => this.destroyTracked(g),
    });
  }

  /** WORMHOLE activation/refresh - a brief pulse along all four board boundaries (never a single line across the board), so the "the world just changed" moment reads spatially rather than only as a headline. */
  private playWormholeActivationPulse(): void {
    if (!this.layout) return;
    const { originX, originY, boardWidth, boardHeight } = this.layout;
    const g = this.scene.add.graphics();
    g.lineStyle(5, GameConfig.colors.wormholeCyan, 0.9);
    g.strokeRoundedRect(originX - 6, originY - 6, boardWidth + 12, boardHeight + 12, 16);
    g.setAlpha(0);
    this.track(g);
    this.scene.tweens.add({
      targets: g,
      alpha: { from: 0, to: 0.85 },
      duration: 180,
      yoyo: true,
      onComplete: () => this.destroyTracked(g),
    });
  }

  /** A restrained motion-streak treatment along the board edges while SPEED TRIAL is active - redrawn every frame only while `speedTrialRemainingMs !== null` (so it freezes automatically on Pause, same as every other real-time visual here), never a camera zoom or an actual speed change. */
  private renderSpeedTrialEdgeOverlay(snapshot: LevelGameSnapshot): void {
    const g = this.speedTrialEdgeOverlay;
    if (snapshot.speedTrialRemainingMs === null || !this.layout) {
      g.setVisible(false).clear();
      return;
    }
    g.setVisible(true).clear();
    const { originX, originY, boardWidth, boardHeight } = this.layout;
    const phase = (this.scene.time.now / 90) % 24;
    g.lineStyle(3, SPEED_TRIAL_COLOR, 0.45);
    const dash = 10;
    const gap = 14;
    for (let x = -phase; x < boardWidth; x += dash + gap) {
      g.lineBetween(originX + x, originY - 4, originX + Math.min(x + dash, boardWidth), originY - 4);
      g.lineBetween(originX + x, originY + boardHeight + 4, originX + Math.min(x + dash, boardWidth), originY + boardHeight + 4);
    }
  }

  /**
   * A small bump/flash on GameScene's own authoritative HUD score text -
   * the controller owns the transient tween lifecycle (kill-before-
   * replace), GameScene still owns the Text object and its actual numeric
   * content (already updated by the time this is called - see the call
   * site in `GameScene.handleTickResult()`). Strength scales modestly
   * with `tier`, never enough to make the number hard to read.
   *
   * PR31 follow-up (score feedback): whenever `event` actually increased
   * Bite Score (`biteScoreDelta(event) > 0` - never for SHRINK/SCORE HIT,
   * which already have their own dedicated negative-result feedback
   * elsewhere), this ALSO briefly tints the score text toward a semantic
   * accent color (`scoreFeedbackAccent()` - ordinary gain green, RUSH
   * gold/yellow, JACKPOT gold) for the same short pulse, then restores
   * the default color, and shows a small "+N" that rises and fades next
   * to it. One reused Text object (`scoreDeltaText`), never a fresh one
   * per bite - no accumulating GameObjects across a long Endless run.
   * Deliberately NOT a per-frame animation: exactly one short Tween per
   * bite, immediately superseded (kill-before-replace) by the next one if
   * bites happen in rapid succession.
   */
  pulseScoreHud(target: Phaser.GameObjects.Text, tier: number, event: BiteFeedbackEvent): void {
    this.scene.tweens.killTweensOf(target);
    const t = Math.min(Math.max(tier, 1), 9);
    target.setScale(1);

    const delta = biteScoreDelta(event);
    const isPositiveGain = delta > 0;
    if (isPositiveGain) {
      const accentColor = SCORE_FEEDBACK_ACCENT_COLOR[scoreFeedbackAccent(event)];
      target.setColor(hexToCss(accentColor));
      this.showScoreDelta(target, delta, accentColor);
    }

    this.scene.tweens.add({
      targets: target,
      scale: { from: 1, to: 1.06 + t * 0.015 },
      duration: 90,
      yoyo: true,
      ease: "Sine.easeOut",
      onComplete: () => {
        if (isPositiveGain) target.setColor(DEFAULT_TEXT_COLOR);
      },
    });
  }

  /** The rising/fading "+N" next to a positive score bump - see `pulseScoreHud()`'s own doc. Positioned just above the score text's own top-left, independent of its current width (which varies with the live RUSH badge suffix, e.g. "SCORE 40   RUSH x2 · SPEED +20%") so it never drifts far off past a long badge string. */
  private showScoreDelta(target: Phaser.GameObjects.Text, delta: number, accentColor: number): void {
    this.scene.tweens.killTweensOf(this.scoreDeltaText);
    const startY = target.y - 16;
    this.scoreDeltaText.setText(`+${delta}`).setColor(hexToCss(accentColor)).setPosition(target.x, startY).setAlpha(1).setScale(1);
    this.scene.tweens.add({
      targets: this.scoreDeltaText,
      y: startY - 14,
      alpha: { from: 1, to: 0 },
      duration: 500,
      ease: "Cubic.easeOut",
      onComplete: () => this.scoreDeltaText.setAlpha(0),
    });
  }

  // ---------------------------------------------------------------------
  // QA gallery (`?qa=feedback`) - every trigger is visual-only: it calls
  // straight into this controller with synthetic data, never touches
  // `LevelGame`, persistence, analytics, or the network.
  // ---------------------------------------------------------------------

  /** A fixed, safely-in-bounds demo cell for every QA trigger's spatial effects - deliberately not tied to the real snake/level, so the QA gallery's synthetic triggers can never touch real gameplay state. */
  private static readonly QA_DEMO_POINT: Point = { x: 4, y: 4 };

  /** Synthetic spatial context for a QA trigger - a small fake snake body trailing behind `QA_DEMO_POINT` so RUSH's trail and SHRINK's ghost segments have something real to animate, entirely disconnected from `LevelGame`. */
  private qaDemoContext(wormholeAlreadyActiveBefore = false): BiteImpactContext {
    const p = VisualFeedbackController.QA_DEMO_POINT;
    const snakeAfter: Point[] = [p, { x: p.x - 1, y: p.y }, { x: p.x - 2, y: p.y }];
    const snakeBefore: Point[] = [...snakeAfter, { x: p.x - 3, y: p.y }, { x: p.x - 4, y: p.y }];
    return { atPoint: p, wormholeAlreadyActiveBefore, snakeAfter, snakeBefore, direction: "right" };
  }

  triggerQa(id: string): void {
    const center: Point = VisualFeedbackController.QA_DEMO_POINT;
    switch (id) {
      case "safe":
        this.onBiteEvent({ kind: "safe", growth: 1, score: 1 }, this.qaDemoContext());
        return;
      case "rush-1":
      case "rush-2":
      case "rush-3":
      case "rush-4":
      case "rush-5": {
        const stack = Number(id.split("-")[1]);
        this.onBiteEvent({ kind: "rush", stack, growth: 2, score: 4 * stack, speedBoostPercent: 10 * stack }, this.qaDemoContext());
        return;
      }
      case "chaos-anticipation":
        this.headlineQueue.enqueue({ payload: { kind: "chaos-anticipation", text: "CHAOS!" }, priority: FeedbackPriority.majorEvent, holdMs: 600 });
        this.renderHeadlineLane();
        return;
      case "chaos-jackpot":
      case "jackpot":
        this.onBiteEvent({ kind: "chaos", outcome: "jackpot", growth: 3, score: 30 }, this.qaDemoContext());
        return;
      case "chaos-wormhole":
      case "wormhole-activate":
        this.onBiteEvent({ kind: "chaos", outcome: "wormhole", durationSeconds: 10 }, this.qaDemoContext());
        return;
      case "wormhole-refresh":
        this.onBiteEvent({ kind: "chaos", outcome: "wormhole", durationSeconds: 10 }, this.qaDemoContext(true));
        return;
      case "chaos-shrink":
      case "shrink":
        this.onBiteEvent({ kind: "chaos", outcome: "shrink", segmentsRemoved: 2, scoreLoss: 10 }, this.qaDemoContext());
        return;
      case "shrink-min":
        // At minimum length the engine removes nothing - never invents removal geometry, so this must produce the smaller failed-contract pulse instead of ghost segments.
        this.onBiteEvent({ kind: "chaos", outcome: "shrink", segmentsRemoved: 0, scoreLoss: 5 }, this.qaDemoContext());
        return;
      case "chaos-speed-trial":
      case "speed-trial-start":
        this.onBiteEvent({ kind: "chaos", outcome: "speed-trial", durationSeconds: 8, rewardGrowth: 3, rewardScore: 30 }, this.qaDemoContext());
        return;
      case "chaos-score-hit":
        this.onBiteEvent({ kind: "chaos", outcome: "score-hit", previousScore: 5, newScore: 0, delta: -5, wasFlat: true, percent: 25 }, this.qaDemoContext());
        return;
      case "speed-trial-success":
        this.onBiteEvent({ kind: "speed-trial-success", rewardGrowth: 4, rewardScore: 40 }, this.qaDemoContext());
        return;
      case "speed-trial-active":
        this.qaBadgeOverride["speed-trial"] = { text: "SPEED TRIAL — 5s", warning: false, color: SPEED_TRIAL_COLOR };
        return;
      case "speed-trial-warning":
        this.qaBadgeOverride["speed-trial"] = { text: "SPEED TRIAL — 1s", warning: true, color: SPEED_TRIAL_COLOR };
        return;
      case "speed-trial-exit":
        this.qaBadgeOverride["speed-trial"] = undefined;
        this.statusTexts["speed-trial"].setVisible(false);
        return;
      case "wormhole-active":
        this.qaBadgeOverride.wormhole = { text: "WORMHOLE — 8s", warning: false, color: GameConfig.colors.wormholeCyan };
        return;
      case "wormhole-warning":
        this.qaBadgeOverride.wormhole = { text: "WORMHOLE — 2s", warning: true, color: GameConfig.colors.wormholeCyan };
        return;
      case "wormhole-wrap-up":
        this.onWormholeWrap({ x: 0, y: 5 }, { x: 0, y: 0 });
        return;
      case "wormhole-wrap-down":
        this.onWormholeWrap({ x: 0, y: 0 }, { x: 0, y: 5 });
        return;
      case "wormhole-wrap-left":
        this.onWormholeWrap({ x: 5, y: 0 }, { x: 0, y: 0 });
        return;
      case "wormhole-wrap-right":
        this.onWormholeWrap({ x: 0, y: 0 }, { x: 5, y: 0 });
        return;
      case "wormhole-bite":
        this.onBiteBonus({ kind: "trick", points: 20, source: "wormhole" });
        return;
      case "wormhole-clutch":
        this.onBiteBonus({ kind: "clutch", points: 35, source: "wormhole" });
        return;
      case "starfruit-appear":
        this.playPulse(center, GameConfig.colors.starfruitGlow, false);
        return;
      case "starfruit-eat":
        // Demoes the full eat -> gate energy travel -> ripple sequence -
        // needs a real gate position, so temporarily aim it at a demo
        // cell offset from the eaten fruit if none was ever set (e.g. a
        // survival level with no gate).
        this.onBiteEvent({ kind: "starfruit", gateDurationSeconds: 12 }, this.qaDemoContext());
        return;
      case "gate-temp-open":
        this.qaBadgeOverride["starfruit-gate"] = { text: "STARFRUIT GATE — 12s", warning: false, color: GameConfig.colors.starfruitGlow };
        this.gateGlow.setState("open-temporary");
        return;
      case "gate-temp-warning":
        this.qaBadgeOverride["starfruit-gate"] = { text: "GATE CLOSING — 2s", warning: true, color: GameConfig.colors.starfruitGlow };
        this.gateGlow.setState("warning");
        return;
      case "gate-temp-close":
        this.onStarfruitGateExpired();
        this.qaBadgeOverride["starfruit-gate"] = undefined;
        this.gateCountdownText.setVisible(false);
        this.gateGlow.setState("locked");
        return;
      case "gate-permanent-open":
        this.gateGlow.setState("open-permanent");
        return;
      case "gate-temp-to-permanent":
        // Demoes the promotion path: a temporary gate open first, then
        // immediately promoted - the temporary countdown/warning must
        // disappear and transition cleanly, with no "GATE CLOSED!" shown.
        this.qaBadgeOverride["starfruit-gate"] = { text: "STARFRUIT GATE — 4s", warning: false, color: GameConfig.colors.starfruitGlow };
        this.gateGlow.setState("open-temporary");
        this.qaTimers.push(
          this.scene.time.delayedCall(400, () => {
            this.qaBadgeOverride["starfruit-gate"] = undefined;
            this.gateCountdownText.setVisible(false);
            this.gateGlow.setState("open-permanent");
          }),
        );
        return;
      case "gate-entry":
        this.onGateEntry();
        return;
      case "death":
        this.onDeath();
        return;
      case "level-complete":
      case "new-star-record":
      case "new-high-score":
      case "both-records":
        this.onLevelComplete();
        return;
      case "priority-stress":
        this.onBiteEvent({ kind: "safe", growth: 1, score: 1 }, this.qaDemoContext());
        this.onBiteEvent({ kind: "rush", stack: 3, growth: 2, score: 12, speedBoostPercent: 30 }, this.qaDemoContext());
        this.onBiteEvent({ kind: "chaos", outcome: "jackpot", growth: 3, score: 30 }, this.qaDemoContext());
        this.onDeath();
        return;
      case "cleanup-reset":
        this.resetForNewRun();
        return;
      default:
        return;
    }
  }

  // ---------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------

  /** Skill lane, near-snake lane, status badges, and the WORMHOLE snake overlay - everything EXCEPT the headline lane, so a caller about to show its own headline (gate entry/death/level-complete) can clear every stale ambient effect first without wiping the headline it's about to enqueue. */
  private clearAmbientOnly(): void {
    this.skillQueue.clear();
    this.nearSnakeQueue.clear();
    this.scene.tweens.killTweensOf(this.skillText);
    this.scene.tweens.killTweensOf(this.nearSnakeText);
    for (const t of Object.values(this.statusTexts)) this.scene.tweens.killTweensOf(t);
    this.skillText.setAlpha(0);
    this.nearSnakeText.setAlpha(0);
    this.hideStatusLane();
    this.wormholeSnakeOverlay.setVisible(false).clear();
    this.speedTrialEdgeOverlay.setVisible(false).clear();
    for (const g of this.rushTrailPool) {
      this.scene.tweens.killTweensOf(g);
      g.setVisible(false);
    }
  }

  private clearEverything(): void {
    this.clearAmbientOnly();
    this.headlineQueue.clear();
    this.scene.tweens.killTweensOf(this.headlineText);
    this.headlineText.setAlpha(0);
    this.scene.tweens.killTweensOf(this.scoreDeltaText);
    this.scoreDeltaText.setAlpha(0);
    this.scene.tweens.killTweensOf(this.goalHintText);
    this.goalHintText.setAlpha(0);
  }

  /** Restart/new run - clears every queue/badge/overlay this controller owns, kills every tween running on a pooled object, and destroys every transient object still in flight - but keeps the pooled Text/Graphics objects themselves alive (reused, never recreated) for the next run. */
  resetForNewRun(): void {
    this.clearEverything();
    this.gateGlow.reset();
    for (const obj of [...this.transient]) this.destroyTracked(obj);
    for (const t of this.qaTimers) t.remove();
    this.qaTimers.length = 0;
  }

  /** Scene shutdown/transition - releases every pooled object and tween outright. After this, the controller must not be used again. */
  destroy(): void {
    this.resetForNewRun();
    this.headlineText.destroy();
    this.goalHintText.destroy();
    this.scoreDeltaText.destroy();
    this.skillText.destroy();
    this.nearSnakeText.destroy();
    for (const t of Object.values(this.statusTexts)) t.destroy();
    this.gateCountdownText.destroy();
    this.wormholeSnakeOverlay.destroy();
    this.speedTrialEdgeOverlay.destroy();
    for (const g of this.rushTrailPool) {
      this.scene.tweens.killTweensOf(g);
      g.destroy();
    }
    this.rushTrailPool.length = 0;
  }

  private track(obj: Phaser.GameObjects.GameObject): void {
    this.transient.add(obj);
  }

  private destroyTracked(obj: Phaser.GameObjects.GameObject): void {
    this.transient.delete(obj);
    if (!obj.scene) return; // already destroyed
    obj.destroy();
  }
}
